// src/pages/Competitions/CompetitionPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getUserCompetitions,
  addCompetition,
  deleteCompetitionCascade,
  resetCompetitionBrackets,
} from "../../services/competitionsServices";
import { addCompetitionSetting } from "../../services/competitionsettingsServices";
import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import useScrollToTop from "../../hooks/useScrollToTop";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import { QRCodeCanvas } from "qrcode.react";
import { ToastContainer, toast } from "react-toastify";

import Toolbar from "./components/Toolbar";
import CreateCompetitionDrawer from "./components/CreateCompetitionDrawer";
import CompetitionCard from "./components/CompetitionCard";

import EditCompetitionAdminModal from "./EditCompetitionModal";
import EditCompetitionSettingsModal from "./EditCompetitionSettingsModal";
import { notifyAdmin } from "../../utils/adminNotify";

const PUBLIC_APP_URL = (
  process.env.REACT_APP_PUBLIC_APP_URL || "https://www.scoreboard-judo-club.fr"
).replace(/\/$/, "");

const buildPublicUrl = (path = "") => {
  const cleanPath = String(path).startsWith("/") ? path : `/${path}`;
  return `${PUBLIC_APP_URL}${cleanPath}`;
};

const CompetitionPage = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const { user } = useUser();

  const userId = user?.userId;
  const userRole = getRoleFromRoleId(user?.role_id);
  const isDemoUser = user?.role_id === 2;
  const isClientUser = user?.role_id === 3;
  const isFullAccessUser = user?.role_id === 4 || user?.role_id === 1;
  const canReset = [1, 2, 3, 4].includes(user?.role_id);

  const [competitions, setCompetitions] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [currentCompetitionId, setCurrentCompetitionId] = useState(null);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState(null);

  const [newCompetition, setNewCompetition] = useState({
    nom_competition: "",
    level_competition: "",
    lieu_competition: "",
    date_competition: "",
    organisateur_competition: userId,
    horaires_pesees: "",
    photo_URL: "",
    is_active: !(user?.role_id === 2 || user?.role_id === 3),
    is_public: !(user?.role_id === 2 || user?.role_id === 3),
    disciplines_competition: ["Judo"],
  });

  const fetchUserCompetitions = async () => {
    try {
      const data = await getUserCompetitions(userId);
      setCompetitions(data.map((c) => ({ ...c, clubInput: "" })));
    } catch {
      toast.error("Erreur lors du chargement des compétitions.");
    }
  };

  useEffect(() => {
    if (!userId) return;
    fetchUserCompetitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const filtered = useMemo(() => {
    return competitions.filter((c) => {
      const q =
        `${c.nom_competition} ${c.level_competition} ${c.lieu_competition}`
          .toLowerCase()
          .includes(search.toLowerCase());

      const s =
        statusFilter === "all"
          ? true
          : statusFilter === "active"
            ? !!c.is_active
            : !c.is_active;

      return q && s;
    });
  }, [competitions, search, statusFilter]);

  const handleOpenEditModal = (id) => {
    setCurrentCompetitionId(id);
    setEditModalOpen(true);
  };

  const handleOpenSettingsModal = (id) => {
    setSelectedCompetitionId(id);
    setSettingsModalOpen(true);
  };

  const handleDeleteCompetition = async (id, name) => {
    const confirmationName = prompt(
      `Pour confirmer la suppression, tapez exactement : "${name}"`,
    );

    if (confirmationName !== name) {
      toast.error("Le nom saisi ne correspond pas. Suppression annulée.");
      return;
    }

    const ok = window.confirm(
      "Cette action supprime la compétition et TOUTES ses données. Continuer ?",
    );

    if (!ok) return;

    try {
      await deleteCompetitionCascade(id);
      toast.success("Compétition supprimée.");
      fetchUserCompetitions();
    } catch {
      toast.error("Erreur lors de la suppression.");
    }
  };

  const handleResetCompetition = async (id, name) => {
    if (!canReset) {
      toast.error("Votre rôle ne permet pas de réinitialiser une compétition.");
      return;
    }

    const expected = `RESET ${name}`;
    const typed = prompt(
      `⚠️ Réinitialisation compétition\n\nCette action va :\n- supprimer toutes les distributions\n- supprimer tous les combats\n- remettre à zéro les classements des compétiteurs.\n\nPour confirmer, tapez exactement :\n"${expected}"`,
    );

    if (typed !== expected) {
      toast.error("Le texte saisi ne correspond pas. Reset annulé.");
      return;
    }

    const ok = window.confirm(
      "Confirmez-vous la RÉINITIALISATION ?\n\nLes compétiteurs seront conservés, mais tout le reste (distributions/combats) sera supprimé.",
    );

    if (!ok) return;

    try {
      const result = await resetCompetitionBrackets(id);
      if (!result) throw new Error("Reset échoué");

      toast.success(
        `Compétition réinitialisée ✅ (${result.deleted_distributions ?? 0} distributions, ${result.deleted_combats ?? 0} combats)`,
      );
      fetchUserCompetitions();
    } catch {
      toast.error("Erreur lors de la réinitialisation.");
    }
  };

  const handleViewCompetition = (id) => {
    navigate(`/competition/${id}`);
  };

  const handleDownloadQRCode = (canvasId, filename = "qr_code.png") => {
    const canvas = document.getElementById(canvasId);

    if (!canvas) {
      toast.error("QR code introuvable.");
      return;
    }

    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");

    const link = document.createElement("a");
    link.href = pngUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyInvite = async (competition) => {
    const club = competition.clubInput?.trim();

    if (!club) {
      toast.error("Veuillez saisir un nom de club.");
      return;
    }

    const url = buildPublicUrl(
      `/invitation/${competition.id}?club=${encodeURIComponent(club)}`,
    );

    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien d'invitation club copié !");
    } catch {
      toast.error("Impossible de copier le lien.");
    }
  };

  const handleCopyArrivalRegistration = async (competition) => {
    const url = buildPublicUrl(`/inscription-judoka/${competition.id}`);

    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien d'inscription sur place copié !");
    } catch {
      toast.error("Impossible de copier le lien.");
    }
  };

  const handleChangeClubInput = (id, value) => {
    setCompetitions((prev) =>
      prev.map((c) => (c.id === id ? { ...c, clubInput: value } : c)),
    );
  };

  const handleCreate = async (e) => {
    e?.preventDefault?.();

    if (isDemoUser && competitions.length >= 1) {
      toast.error(
        "En mode démo, vous ne pouvez créer qu’une seule compétition.",
      );
      return;
    }

    if (isClientUser && competitions.length >= 1) {
      toast.error("Votre abonnement autorise une seule compétition.");
      return;
    }

    if (!isFullAccessUser && !isClientUser && !isDemoUser) {
      toast.error("Votre rôle ne permet pas d’ajouter une compétition.");
      return;
    }

    try {
      const created = await addCompetition({ ...newCompetition });
      const newId = created?.id || created?.data?.id;

      if (!newId) throw new Error("ID non renvoyé.");

      toast.success("Compétition ajoutée.");

      await addCompetitionSetting({
        competition_id: newId,
        temps_baby: 90,
        temps_minipoussin: 90,
        temps_poussin: 90,
        temps_benjamin: 120,
        temps_minime: 180,
        temps_cadet: 240,
        temps_junior: 240,
        temps_senior: 240,
        wazari_awasete_ippon: true,
        ippon_met_fin: true,
        deux_ippon_fin: false,
        points_met_fin: false,
        points_fin: 10,
      });

      const creatorName = user
        ? `${user.prenom || ""} ${user.nom || ""}`.trim()
        : `Utilisateur #${userId}`;

      const compName =
        newCompetition.nom_competition || `Compétition #${newId}`;
      const compDate = newCompetition.date_competition || "Date non renseignée";
      const compLieu = newCompetition.lieu_competition || "Lieu non renseigné";
      const compLevel =
        newCompetition.level_competition || "Niveau non précisé";

      const message = `Nouvelle compétition créée :
- Nom : ${compName}
- Niveau : ${compLevel}
- Date : ${compDate}
- Lieu : ${compLieu}
- Créée par : ${creatorName} (ID ${userId})`;

      await notifyAdmin(message);

      setNewCompetition({
        nom_competition: "",
        level_competition: "",
        lieu_competition: "",
        date_competition: "",
        organisateur_competition: userId,
        horaires_pesees: "",
        photo_URL: "",
        is_active: !(user?.role_id === 2 || user?.role_id === 3),
        is_public: !(user?.role_id === 2 || user?.role_id === 3),
        disciplines_competition: ["Judo"],
      });

      setDrawerOpen(false);
      fetchUserCompetitions();
    } catch {
      toast.error("Erreur lors de la création.");
    }
  };

  const total = competitions.length;
  const actives = competitions.filter((c) => c.is_active).length;

  return (
    <div className="flex flex-col w-full mx-auto p-3 mt-[130px] dark:text-white">
      <ScrollIndicator />

      <div className="mx-auto w-full max-w-[1280px]">
        <header className="text-center mb-6">
          <h2 className="text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
            Organisateur — {userRole}
          </h2>

          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
            Mes Compétitions
          </h1>

          {isDemoUser && (
            <div className="mt-3 rounded-xl border border-yellow-300/60 bg-yellow-50/80 text-yellow-800 p-3 text-sm">
              Mode Démo : limité à 1 compétition (données fictives uniquement).
            </div>
          )}
        </header>

        <Toolbar
          total={total}
          actives={actives}
          search={search}
          onSearch={setSearch}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          onNew={() => setDrawerOpen(true)}
        />

        {filtered.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-gray-300 dark:border-slate-700 p-10 text-center">
            <p className="text-gray-600 dark:text-gray-300">
              Aucune compétition trouvée. Cliquez sur{" "}
              <strong>Nouvelle compétition</strong> pour commencer.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {filtered.map((c) => (
              <CompetitionCard
                key={c.id}
                competition={c}
                publicAppUrl={PUBLIC_APP_URL}
                onEdit={handleOpenEditModal}
                onSettings={handleOpenSettingsModal}
                onDelete={handleDeleteCompetition}
                onReset={handleResetCompetition}
                canReset={canReset}
                onView={handleViewCompetition}
                onCopyInvite={handleCopyInvite}
                onCopyArrivalRegistration={handleCopyArrivalRegistration}
                onChangeClubInput={handleChangeClubInput}
                onDownloadQR={handleDownloadQRCode}
                QRComponent={QRCodeCanvas}
              />
            ))}
          </div>
        )}
      </div>

      <CreateCompetitionDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        newCompetition={newCompetition}
        setNewCompetition={setNewCompetition}
        onSubmit={handleCreate}
        isDemoUser={isDemoUser}
        isClientUser={isClientUser}
      />

      <EditCompetitionAdminModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        refreshList={fetchUserCompetitions}
        competitionId={currentCompetitionId}
      />

      <EditCompetitionSettingsModal
        isOpen={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        competitionId={selectedCompetitionId}
      />

      <ToastContainer />
    </div>
  );
};

export default CompetitionPage;
